/**
 * Plugin Modal Bridge
 *
 * Module-level bridge that allows the non-React PluginBridge class
 * to open/close modals through the React component tree.
 *
 * Consumers listen for modal events via subscribe() and render
 * the appropriate modal UI. This decouples the PluginBridge (class)
 * from React rendering entirely.
 *
 * Wave 14e: Plugin System Hardening
 */

export interface PluginModalRequest {
  pluginId: string;
  modalId: string;
  props?: Record<string, any>;
}

type ModalOpenHandler = (request: PluginModalRequest) => void;
type ModalCloseHandler = () => void;

let _onOpen: ModalOpenHandler = (req) =>
  console.log('[Plugin Modal] open:', req.modalId, req.props);
let _onClose: ModalCloseHandler = () =>
  console.log('[Plugin Modal] close');
let _connected = false;

export const pluginModalBridge = {
  /**
   * Open a plugin modal. Called from PluginBridge handlers.
   */
  open(pluginId: string, modalId: string, props?: Record<string, any>) {
    _onOpen({ pluginId, modalId, props });
  },

  /**
   * Close the current plugin modal.
   */
  close() {
    _onClose();
  },

  /** Whether the bridge is connected to a real modal renderer */
  get isConnected() {
    return _connected;
  },

  /**
   * Wire the bridge to the real modal system.
   * Called once from inside the React tree.
   */
  connect(handlers: {
    onOpen: ModalOpenHandler;
    onClose: ModalCloseHandler;
  }) {
    _onOpen = handlers.onOpen;
    _onClose = handlers.onClose;
    _connected = true;
  },

  /**
   * Disconnect. Falls back to console.
   */
  disconnect() {
    _onOpen = (req) => console.log('[Plugin Modal] open:', req.modalId, req.props);
    _onClose = () => console.log('[Plugin Modal] close');
    _connected = false;
  },
};
